<template>
  <div class="mobile-app">
    <div class="public-page-content">
      <div class="description">
        <h1 class="public-page-title">
          {{ $t("mobile-app-page-title") }}
        </h1>
        <ul class="features">
          <li
            v-for="item in features"
            :key="item.imgSrc"
          >
            <figure>
              <div class="illustration">
                <img
                  :alt="item.caption.replace('<br>', ';')"
                  :src="item.imgSrc"
                >
              </div>
              <figcaption>{{ $t(item.caption) }}</figcaption>
            </figure>
          </li>
        </ul>
        <div
          :class="{
            'pack-up-android': showMoreInstallChannels && isAndroid,
            'pack-up-ios': showMoreInstallChannels && !isAndroid,
          }"
          class="install-channels"
        >
          <div class="install-left">
            <QrCode :link="`https://${domain}/mobile-app?action=install`" />
          </div>
          <ul class="install-right">
            <li
              v-for="item in installChannels.filter((i) => !i.hide)"
              :key="item.imgSrc.value"
            >
              <a
                :download="/\.apk/i.test(item.href) ? 'PikPak.apk' : undefined"
                :href="item.href"
                :style="{
                  'background-image': `url(${item.imgSrc.value || ''})`,
                }"
                @click="
                  $track.web_official_mobile_page_click({
                    button: item.trackEventButtonParameter,
                  })
                "
              />
            </li>
          </ul>

          <!-- 移动端悬浮 -->
          <div
            class="controller"
            @click="showMoreInstallChannels = !showMoreInstallChannels"
          >
            <span>{{
              showMoreInstallChannels ? $t("show-more") : $t("show-less")
            }}</span>
            <i
              :class="{
                'icon-down': showMoreInstallChannels,
                'icon-up': !showMoreInstallChannels,
              }"
              class="icon"
            />
          </div>
        </div>
      </div>
      <img
        v-if="isMobile"
        :alt="$t('mobile-app-page-title')"
        class="description-pic"
        src="@/assets/images/mobile-app/mobile-app.m.png"
        style="margin: 16px auto 32px"
      >
      <img
        v-else
        :alt="$t('mobile-app-page-title')"
        class="description-pic"
        src="@/assets/images/mobile-app/mobile-app.png"
      >
    </div>
  </div>
</template>

<script lang="ts" setup>
import {
  definePageMeta,
  isIOS,
  onMounted,
  toRefs,
  useI18n,
  useSeoMeta,
} from '#imports';
import QrCode from '@/components/home/QrcodeNormal.vue';
import useDomain from '@/composables/useDomain';
import useInstallChannels, {
  appInstallLink,
  osNameInServerAndClient,
} from '@/composables/useInstallChannels';
import { features } from '@/config';
import $track from '@/track/webOfficialWebsite';
import { AppInstallPlatform, OSName } from '@/types/enum';
import ogImage from 'assets/images/mobile-app/mobile-app.png';
import { ref } from 'vue';
import usePageTitle from '~/composables/usePageTitle';
import { useLayout } from '~/store/layout';

const pageTitle = usePageTitle(useI18n().t('basic-mobile-app'));

useSeoMeta({
  title: pageTitle,
  ogTitle: pageTitle,
  ogImage: { url: ogImage, width: 1385, height: 1500 },
});
const installChannels = useInstallChannels();
const domain = useDomain();
const { isMobile } = toRefs(useLayout());

const showMoreInstallChannels = ref(true);

const isAndroid = osNameInServerAndClient() === OSName.ANDROID;

const SUPPORT_ACTIONS = ['install'];

const handlePageInitAction = () => {
  try {
    const params = new URLSearchParams(
      window.location.search.toLocaleLowerCase(),
    );
    const action = params.get('action');
    if (action && SUPPORT_ACTIONS.includes(action)) {
      switch (action) {
        case 'install':
          window.location.href = appInstallLink(
            isIOS()
              ? AppInstallPlatform.APPLE_STORE
              : AppInstallPlatform.GOOGLE_PLAY,
          );
          break;
        default:
      }
    }
  } catch (err) {
    console.error('[handlePageInitAction: ]', err);
  }
};

onMounted(() => {
  handlePageInitAction();
});
definePageMeta({
  layout: 'basic',
});
</script>

<style lang="less" scoped>
@import "@/assets/styles/util.less";

.mobile-app {
  background: url("@/assets/images/desktop-app/background.png")
    var(--no-repeat-center) / cover;
  box-sizing: border-box;
  display: flex;
  flex: 1 0 100%;
  flex-direction: column;

  .controller {
    display: none;
  }
}

.public-page-content {
  height: 100%;
  display: flex;
  align-items: center;
  max-width: calc(var(--pc-middle-width) + 40px);
  justify-content: space-between;
}

.description {
  max-width: 600px;
}

.description-pic {
  width: 560px;
  height: 530px;
}

.public-page-title {
  font-size: 32px;
  line-height: 45px;
}

.features {
  margin-top: 32px;
  display: flex;
  width: 100%;
  justify-content: space-evenly;
  text-align: center;

  li {
    flex: 0 0 30%;
  }

  .illustration {
    display: flex;
    height: 84px;
    justify-content: center;
  }

  figcaption {
    font-weight: 400;
    font-size: 16px;
    line-height: 23px;
    margin-top: 24px;
  }
}

.install-channels {
  display: flex;
  margin-top: 28px;
  margin-bottom: 10px;
  justify-content: flex-start;

  a {
    display: block;
    width: 180px;
    height: 54px;
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
  }

  .install-left {
    width: 180px;
    height: 160px;

    .qr-code {
      width: 100%;
      height: 180px;
      transform: translateY(-10px);
    }
  }

  .install-right {
    margin-left: 24px;
    width: 380px;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;

    .controller {
      display: none;
    }
  }
}

@media screen and (max-width: @pc-min-width) {
  .description-pic {
    width: 320px;
    height: 303px;
  }
}

@media screen and (max-width: @mobile-break-width) {
  .mobile-app {
    flex: calc(var(--vh, 100vh) - var(--header-height) - var(--footer-height));
    overflow-y: scroll;
    background: #f5f6fa;
    background: url("@/assets/images/desktop-app/mobile-bg.png")
      var(--no-repeat-center) / cover;
  }

  .public-page-content {
    margin-top: 0;
    flex-wrap: wrap;
    align-items: flex-start;
    height: auto;
    box-sizing: border-box;
    padding-bottom: 100px;
  }

  .description {
    width: 100%;

    .public-page-title {
      width: 277px;
      font-size: 20px;
      text-align: center;
      font-weight: 600;
      line-height: 28px;
      margin: 36px auto 0 auto;
    }
  }

  .features {
    .illustration {
      height: 64px;

      img {
        width: 64px;
        height: 64px;
      }

      figcaption {
        width: 80px;
        margin-top: 24px;
        font-size: 16px;
      }
    }
  }

  .description-pic {
    width: 320px;
    max-width: 100%;
  }

  .install-channels {
    position: fixed;
    z-index: 2;
    margin-top: 0;
    padding-top: 20px;
    background-color: #fff;
    width: 100vw;
    height: 310px;
    left: 0;
    bottom: calc(var(--footer-height) - 10px);
    align-content: center;
    overflow: hidden;
    transition: height 0.1s ease-in;

    &.pack-up-android {
      height: 180px;
    }

    &.pack-up-ios {
      height: 120px;
    }

    .install-left {
      display: none;
    }

    .install-right {
      position: absolute;
      top: 20px;
      left: 50%;
      transform: translateX(-50%);
      flex-direction: column;
      flex-wrap: nowrap;
      justify-content: center;
      margin-left: 0;

      & > li {
        margin-bottom: 8px;
      }

      a {
        width: 210px;
        height: 54px;
      }
    }

    .controller {
      display: flex;
      position: absolute;
      left: 0;
      bottom: 0;
      width: 100%;
      height: 40px;
      justify-content: center;
      align-items: center;
      background: #fff;
      font-weight: 400;
      font-size: 13px;
      color: #306eff;
    }

    .icon {
      display: block;
      width: 24px;
      height: 24px;
      background: url("@/assets/images/mobile-app/down.svg") no-repeat
        center/cover;
    }

    .icon-up {
      background: url("@/assets/images/mobile-app/up.svg") no-repeat
        center/cover;
    }

    .icon-down {
      background: url("@/assets/images/mobile-app/down.svg") no-repeat
        center/cover;
    }
  }
}
</style>
